import clsx from 'clsx';
import { Label } from '../Label';
import { HTMLInputTypeAttribute, ReactNode, useEffect, useRef } from 'react';
import { randomNumber } from '../../lib/constants';

interface Props extends BaseProps {
  align?: 'left' | 'center' | 'right';
  autoFocus?: boolean;
  endAdornment?: ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string | null;
  label?: string | null;
  startAdornment?: ReactNode;
  type?: HTMLInputTypeAttribute;
  value?: string | number;
  variant?: 'default' | 'contained';
  dark?: boolean;
}

export const Input = ({
  align = 'left',
  autoFocus = false,
  className,
  disabled = false,
  endAdornment = null,
  error = false,
  fullWidth = false,
  helperText = null,
  inputClassName,
  label = null,
  required,
  startAdornment = null,
  type = 'text',
  value,
  variant = 'default',
  dark = false,
  ...rest
}: Props) => {
  const formattedLabel = label
    ? label.toLowerCase().trim().replaceAll(' ', '-')
    : undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const width = fullWidth ? 'full' : 'unset';
  const classes = useStyles();

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const renderHelperText = () => {
    if (!helperText) return;
    return (
      <p
        className={clsx(
          classes.helperText.default,
          classes.helperText[error ? 'error' : 'info']
        )}
      >
        {helperText}
      </p>
    );
  };

  const input = (
    <div
      className={clsx(
        'transition-all ease-in-out duration-300 flex flex-row gap-2 focus-within:ring-1 ring-primary',
        classes.input[variant],
        {
          [classes.input.error]: error,
          'opacity-40': disabled,
          'ring-1 ring-error': variant === 'contained' && error,
          '!bg-[#171717] ring-1 !border-transparent focus-within:ring-primary ring-border-dark text-white':
            dark,
        }
      )}
    >
      {startAdornment}
      <input
        ref={inputRef}
        id={`${label ? formattedLabel : randomNumber()}-input`}
        autoFocus={autoFocus}
        aria-disabled={disabled}
        aria-labelledby={label ? `label-${formattedLabel}` : undefined}
        aria-required={required}
        aria-errormessage={helperText ? helperText : `Error with ${label}`}
        className={clsx(
          'bg-transparent w-full p-0 border-0 focus:ring-0 text-base md:text-sm outline-0 min-h-[22px]',
          {
            [classes.input.error]: error,
            [classes.align[align]]: align,
            [classes.file]: type === 'file',
          }
        )}
        disabled={disabled}
        required={required}
        tabIndex={0}
        type={type}
        value={value}
        {...rest}
      />
      {endAdornment}
    </div>
  );

  const renderInput = () => {
    if (label)
      return (
        <Label
          id={`label-${formattedLabel}`}
          className={classes.width[width]}
          aria-disabled={disabled}
          aria-label={label}
          text={label}
          required={required}
          disabled={disabled}
        >
          {input}
          {renderHelperText()}
        </Label>
      );
    return (
      <div className={classes.width[width]}>
        {input}
        {renderHelperText()}
      </div>
    );
  };

  return (
    <div className={clsx(className, classes.container, classes.width[width])}>
      {renderInput()}
    </div>
  );
};

const useStyles = () => ({
  align: {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  },
  container: 'block p-1 box-border',
  file: 'file:rounded file:bg-slate-200 file:dark:bg-background-dark file:border-0 file:text-inherit file:cursor-pointer file:text-inherit file:dark:text-primary file:hover:dark:bg-background-dark/70',
  helperText: {
    default: 'text-[0.75rem] mt-1',
    info: 'opacity-70',
    error: 'text-error',
  },
  input: {
    default:
      'text-unset w-full bg-transparent border-b border-b-gray-400 dark:border-b-white p-1 focus-within:border-primary',
    contained:
      'text-unset w-full border !border-gray-400 dark:!border-transparent bg-gray-100 dark:bg-[#1B1E20] rounded p-1 border border-transparent focus-within:!border focus-within:!border-primary',
    error: '!border-error focus:!border-error active:!border-error',
  },
  width: {
    full: 'w-full',
    unset: 'w-auto',
  },
});
